const API_URL = import.meta.env.VITE_API_URL || 'https://outpost9-api.outpost9.workers.dev';
export const WORKER_URL = import.meta.env.VITE_WORKER_URL || import.meta.env.VITE_API_URL || 'https://outpost9-api.outpost9.workers.dev';

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers: customHeaders, ...rest } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  const config: RequestInit = {
    ...rest,
    headers,
    credentials: 'include',
  };

  if (body !== undefined) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_URL}${endpoint}`, config);

  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
    }
    throw new ApiError(response.status, response.statusText, data);
  }

  const text = await response.text();
  if (!text) return undefined as T;

  return JSON.parse(text) as T;
}

export const api = {
  auth: {
    getSession: () => request<{ session: { user: import('../types').User } | null }>('/auth/get-session'),
    signOut: () => request('/auth/sign-out', { method: 'POST' }),
  },

  applications: {
    get: (id: string) =>
      request<{
        agent: import('../types').Application;
        latestDeployment: import('../types').Deployment | null;
      }>(`/api/agents/${id}`),

    update: (id: string, data: { name?: string }) =>
      request<{ agent: import('../types').Application }>(`/api/agents/${id}`, {
        method: 'PATCH',
        body: data,
      }),

    delete: (id: string) =>
      request<{ deleted: boolean }>(`/api/agents/${id}`, { method: 'DELETE' }),

    getStats: (id: string) =>
      request<{ stats: import('../types').ApplicationStats }>(`/api/agents/${id}/stats`),

    listDeployments: (id: string) =>
      request<{ deployments: import('../types').Deployment[] }>(`/api/agents/${id}/deployments`),

    listRuns: (id: string, params?: { limit?: number; offset?: number }) => {
      const query = new URLSearchParams();
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.offset) query.set('offset', String(params.offset));
      const qs = query.toString();
      return request<{ runs: import('../types').Run[] }>(
        `/api/agents/${id}/runs${qs ? `?${qs}` : ''}`
      );
    },

    getBindings: (id: string) =>
      request<{ bindings: import('../types').Binding[] }>(`/api/agents/${id}/bindings`),
  },

  runs: {
    get: (runId: string) =>
      request<{ run: import('../types').Run }>(`/runs/${runId}`),

    getLogs: (runId: string) =>
      request<{ logs: import('../types').LogEntry[] }>(`/runs/${runId}/logs`),
  },

  keys: {
    list: (teamId: string) =>
      request<{ keys: import('../types').ApiKey[] }>(`/api/teams/${teamId}/keys`),

    create: (teamId: string, data: { name: string }) =>
      request<{ key: import('../types').ApiKeyWithSecret }>(`/api/teams/${teamId}/keys`, {
        method: 'POST',
        body: data,
      }),

    revoke: (teamId: string, keyId: string) =>
      request<{ deleted: boolean }>(`/api/teams/${teamId}/keys/${keyId}`, {
        method: 'DELETE',
      }),
  },

  teams: {
    list: () => request<{ teams: import('../types').Team[] }>('/api/teams'),
    get: (id: string) => request<{ team: import('../types').Team }>(`/api/teams/${id}`),
  },

  secrets: {
    list: (teamId: string) =>
      request<{ secrets: import('../types').Secret[] }>(`/api/teams/${teamId}/secrets`),
    create: (teamId: string, data: { name: string; value: string }) =>
      request<{ secret: import('../types').SecretResponse }>(`/api/teams/${teamId}/secrets`, {
        method: 'POST',
        body: data,
      }),
    delete: (teamId: string, name: string) =>
      request<{ deleted: boolean }>(`/api/teams/${teamId}/secrets/${name}`, { method: 'DELETE' }),
  },

  projects: {
    list: (teamId: string) =>
      request<{ projects: import('../types').Project[] }>(`/api/teams/${teamId}/projects`),

    get: (teamId: string, projectId: string) =>
      request<{ project: import('../types').Project }>(`/api/teams/${teamId}/projects/${projectId}`),

    listAgents: (projectId: string) =>
      request<{ agents: import('../types').Application[] }>(`/api/projects/${projectId}/agents`),
  },
};

export function getLogStreamUrl(runId: string): string {
  return `${API_URL}/runs/${runId}/logs/stream`;
}

export { API_URL };
